import React, { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useSite } from "./SiteContext";
import { waLink } from "../lib/api";

/**
 * A fixed WhatsApp launcher that follows the guest across every public page.
 * Opens `wa.me` with a pre-filled greeting and a friendly tooltip on hover.
 */
export default function FloatingWhatsApp() {
  const { settings } = useSite();
  const [dismissed, setDismissed] = useState(false);
  const number = settings.whatsapp || "919810701234";
  const href = waLink(
    number,
    "Hello Azulo Travel, I would like to plan a journey."
  );

  return (
    <div
      aria-hidden={false}
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-end px-4 sm:bottom-6 sm:px-6"
      data-testid="floating-whatsapp-wrapper"
    >
      <div className="pointer-events-auto flex items-center gap-2">
        {!dismissed && (
          <div
            className="hidden max-w-[260px] rounded-2xl border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-3 text-xs font-semibold text-[var(--ink-2)] shadow-[0_18px_44px_rgba(14,30,42,0.14)] sm:flex sm:items-center sm:gap-3"
            data-testid="floating-whatsapp-tooltip"
          >
            <span className="leading-snug">
              Chat with a travel designer on WhatsApp
            </span>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss WhatsApp prompt"
              className="rounded-full p-1 text-[var(--muted-ink)] transition-colors hover:bg-[var(--paper-2)] hover:text-[var(--ink)]"
              data-testid="floating-whatsapp-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Chat with Azulo Travel on WhatsApp"
          data-testid="floating-whatsapp-button"
          className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_44px_rgba(37,211,102,0.45)] transition-transform duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brass)]"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 blur-[2px] transition-opacity duration-500 group-hover:opacity-40"
          />
          <MessageCircle className="relative h-6 w-6" strokeWidth={2.25} />
          <span className="sr-only">WhatsApp</span>
        </a>
      </div>
    </div>
  );
}
